import { Checkbox, Typography, Tooltip } from "@mui/material";
import { AlertCircle } from "lucide-react";
import {
  SourcesContainer,
  SourcesList,
  SourceItem,
  SourceItemContent,
  SourceItemText,
  StyledBadge,
} from "./styles";
import { SearchType, SearchTypeText } from "types/enums";
import { IModelDossierViewModel } from "viewModels";

interface ModelListProps {
  models: IModelDossierViewModel[];
  selectedModelId?: string;
  onModelSelect: (model: IModelDossierViewModel) => void;
  maxFonts?: number;
  searchTerm?: string;
}

const ModelList = ({
  models,
  selectedModelId,
  onModelSelect,
  maxFonts = 20,
  searchTerm = "",
}: ModelListProps) => {
  const getTypeBadgeColor = (type: SearchType) => {
    switch (type) {
      case SearchType.BOTH:
        return "#42aa47";
      case SearchType.CNPJ:
        return "#db427a";
      case SearchType.CPF:
        return "#4aa1cf";
      default:
        return "#9E9E9E";
    }
  };

  return (
    <SourcesContainer>
      {models.length === 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
            padding: "16px",
            width: "100%",
          }}
        >
          <AlertCircle size={24} color="#D81B60" />
          <Typography
            variant="body1"
            color="text.secondary"
            style={{ marginTop: "8px" }}
          >
            {searchTerm
              ? `Nenhum modelo encontrado para "${searchTerm}"`
              : "Nenhum item disponível"}
          </Typography>
        </div>
      ) : (
        <SourcesList style={{ width: "100%" }}>
          {models.map((model) => (
            <SourceItem
              key={model.id}
              selected={model.id === selectedModelId}
              onClick={() => !model.disabled && onModelSelect(model)}
              style={{
                opacity: model.disabled ? 0.6 : 1,
                cursor: model.disabled ? "not-allowed" : "pointer",
              }}
            >
              <SourceItemContent>
                <Checkbox
                  checked={model.id === selectedModelId}
                  onChange={() => !model.disabled && onModelSelect(model)}
                  size="small"
                  disabled={model.disabled}
                  sx={{
                    "&.Mui-checked": {
                      color: "#293778",
                    },
                  }}
                />
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <SourceItemText>{model.name}</SourceItemText>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {model.fonts.some((f) => f.disabled) &&
                        !model.disabled && (
                          <Tooltip title="Este modelo contém fontes indisponíveis">
                            <AlertCircle size={16} color="#D81B60" />
                          </Tooltip>
                        )}
                      {model.disabled ? (
                        <Tooltip title="Indisponível">
                          <StyledBadge backgroundColor="#d32f2f">
                            <span>Indisponível</span>
                          </StyledBadge>
                        </Tooltip>
                      ) : (
                        <StyledBadge
                          backgroundColor={getTypeBadgeColor(model.type)}
                        >
                          <span>{SearchTypeText[model.type]}</span>
                        </StyledBadge>
                      )}
                    </div>
                  </div>
                  <Typography variant="caption" color="text.secondary">
                    Contém {model.fonts.length} fonte
                    {model.fonts.length > 1 ? "s" : ""}
                    {model.fonts.length > maxFonts &&
                      ` (Mais de ${maxFonts} fontes podem aumentar o custo)`}
                  </Typography>
                </div>
              </SourceItemContent>
            </SourceItem>
          ))}
        </SourcesList>
      )}
    </SourcesContainer>
  );
};

export default ModelList;
